import { addToRecord, objectWithoutKeys } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { AppViewPropertiesLoadAction } from '../../domains/appViewProperties/actions.types';
import type { AppViewPropertiesSetAction } from '../../domains/appViewProperties/setActions.types';
import type { AppViewPropertiesUpdateAction } from '../../domains/appViewProperties/updateAction.types';
import type { NormalizedAppViewPropertiesEntities } from '../../normalization/types';

import type { AppViewProperties, AppViewProperty, AppViewPropertyKey, AppViewPropertyValue } from './types';
import { NoAppViewPropertyValueOnServer } from './types';

const getAppViewProperty = (
  entityId: string,
  propertyKey: AppViewPropertyKey,
  entities: NormalizedAppViewPropertiesEntities
): AppViewProperty => {
  const value = entities.appViewProperties[propertyKey];
  if (value === undefined) {
    return { id: entityId, key: propertyKey, value: NoAppViewPropertyValueOnServer };
  }

  return value;
};

const addToState = (
  state: AppViewProperties,
  entityId: string,
  propertyKeys: AppViewPropertyKey[],
  entities: NormalizedAppViewPropertiesEntities
) => ({
  ...state,
  ...propertyKeys.reduce(
    (updated, key) => ({
      ...updated,
      [key]: addToRecord(state[key] ?? {}, entityId, getAppViewProperty(entityId, key, entities)),
    }),
    {}
  ),
});

const removeFromState = (state: AppViewProperties, entityId: string, propertyKeys: AppViewPropertyKey[]) => ({
  ...state,
  ...propertyKeys.reduce((updated, key) => {
    if (state[key]) {
      return { ...updated, [key]: objectWithoutKeys(state[key]!, [entityId]) };
    }

    return updated;
  }, {}),
});

const setState = (
  state: AppViewProperties,
  entityId: string,
  propertyKey: AppViewPropertyKey,
  value: AppViewPropertyValue
) => ({
  ...state,
  [propertyKey]: {
    ...state[propertyKey],
    [entityId]: {
      id: entityId,
      key: propertyKey,
      value,
    },
  },
});

const initialState: AppViewProperties = {};

export type AppViewPropertiesActions =
  | AppViewPropertiesLoadAction
  | AppViewPropertiesSetAction
  | AppViewPropertiesUpdateAction;

const reducer: Reducer<AppViewProperties, AppViewPropertiesActions> = (state = initialState, action) => {
  switch (action.type) {
    case 'APP_VIEW_PROPERTIES.LOAD_PENDING':
      return removeFromState(state, action.payload.entityId, action.payload.propertyKeys);
    case 'APP_VIEW_PROPERTIES.LOAD_REJECTED':
      return removeFromState(state, action.payload.entityId, action.payload.propertyKeys);
    case 'APP_VIEW_PROPERTIES.LOAD_FULFILLED':
      return addToState(state, action.payload.entityId, action.payload.propertyKeys, action.payload.entities);
    case 'APP_VIEW_PROPERTIES.SET_FULFILLED':
      return setState(state, action.payload.entityId, action.payload.propertyKey, action.payload.value);
    case 'APP_VIEW_PROPERTIES.UPDATE_STATE_ONLY':
      return setState(state, action.payload.property.id, action.payload.property.key, action.payload.property.value);
    default:
      return state;
  }
};

export default reducer;
